/**
 * The Flow endpoints — slice 69's whole client surface, and the handful of
 * reads the builder needs from other modules.
 *
 *   listFlows            GET    /flows
 *   createFlow           POST   /flows                       always a draft
 *   fetchFlow            GET    /flows/:id                   versions, draft, live
 *   saveFlowDraft        PUT    /flows/:id/draft             the editor's autosave
 *   preflightFlow        POST   /flows/:id/preflight         the checks the sheet lists
 *   launchFlow           POST   /flows/:id/launch            422 when a check blocks
 *   pauseFlow / resumeFlow / archiveFlow
 *   enrolFlow            POST   /flows/:id/enrol             the manual trigger
 *   testFlow             POST   /flows/:id/test              a dry walk; sends nothing
 *   fetchFlowTestPeople  GET    /flows/:id/test-people       who the Test panel can walk as
 *   fetchFlowStats       GET    /flows/:id/stats             run mode
 *   listFlowEnrollments  GET    /flows/:id/enrollments       people per node, masked
 *   fetchFlowEnrollment  GET    /flows/:id/enrollments/:eid  one person's step log
 *   suggestFlowStep      POST   /flows/:id/suggest           deterministic; no AI call
 *
 * **Why the builder reads the field catalogue and the membership events from
 * here rather than from the slice-67 client.** Slices 67 and 69 were built side
 * by side; the editor needs exactly one read of each (the catalogue for a
 * condition's fields and a message's variable sources, the events for test
 * people), and owning two thin wrappers here kept this slice from editing a
 * file another builder was writing at the same time. They parse the same
 * shared contracts, so there is still one shape, not two.
 *
 * **Suggest and preflight read the SAVED draft.** Both take ids, not a graph —
 * the API reads the draft version — so the editor flushes its pending autosave
 * before calling either. A suggestion computed against a graph one keystroke
 * old would offer a step after a node that no longer exists.
 *
 * Request bodies are re-validated against the contract the API validates
 * against, so a body it could never accept never leaves the browser.
 */
import {
  archiveFlowRequestSchema,
  createFlowRequestSchema,
  fieldCatalogueResponseSchema,
  flowEnrollmentDetailResponseSchema,
  flowEnrollmentListResponseSchema,
  flowEnrolRequestSchema,
  flowEnrolResponseSchema,
  flowListResponseSchema,
  flowPreflightResponseSchema,
  flowResponseSchema,
  flowStatsResponseSchema,
  flowSuggestRequestSchema,
  flowSuggestResponseSchema,
  flowTestPeopleResponseSchema,
  flowTestRequestSchema,
  flowTestResponseSchema,
  reachLeadsResponseSchema,
  saveFlowDraftRequestSchema,
  segmentMembershipEventsResponseSchema,
  type CreateFlowRequest,
  type FieldCatalogueResponse,
  type FlowDetail,
  type FlowEnrollmentDetailResponse,
  type FlowEnrollmentListResponse,
  type FlowEnrollmentStatus,
  type FlowEnrolRequest,
  type FlowEnrolResponse,
  type FlowListResponse,
  type FlowPreflightResponse,
  type FlowStatsResponse,
  type FlowSuggestRequest,
  type FlowSuggestResponse,
  type FlowTestPeopleResponse,
  type FlowTestRequest,
  type FlowTestResponse,
  type MaskedPerson,
  type SaveFlowDraftRequest,
} from '@wend/shared';
import { z } from 'zod';

import { apiRequest } from './client';
import { isApiError } from './errors';

/** Ids come from the API, but a path segment is never interpolated raw. */
function flowPath(flowId: string, suffix = ''): string {
  return `/flows/${encodeURIComponent(flowId)}${suffix}`;
}

/** The pause / resume / launch answers carry no new shape: the flow as it now is. */
async function flowCommand(flowId: string, suffix: string, body?: unknown): Promise<FlowDetail> {
  const response = await apiRequest({
    path: flowPath(flowId, suffix),
    method: 'POST',
    ...(body === undefined ? {} : { body }),
    schema: flowResponseSchema,
  });
  return response.flow;
}

/* ---- list, create, read, save -------------------------------------------- */

export async function listFlows(signal?: AbortSignal): Promise<FlowListResponse> {
  return apiRequest({ path: '/flows', schema: flowListResponseSchema, signal });
}

export async function createFlow(request: z.input<typeof createFlowRequestSchema>): Promise<FlowDetail> {
  const body: CreateFlowRequest = createFlowRequestSchema.parse(request);
  const response = await apiRequest({ path: '/flows', method: 'POST', body, schema: flowResponseSchema });
  return response.flow;
}

export async function fetchFlow(flowId: string, signal?: AbortSignal): Promise<FlowDetail> {
  const response = await apiRequest({ path: flowPath(flowId), schema: flowResponseSchema, signal });
  return response.flow;
}

/** The autosave. Creates the draft version when the flow is live and has none. */
export async function saveFlowDraft(flowId: string, request: SaveFlowDraftRequest): Promise<FlowDetail> {
  const response = await apiRequest({
    path: flowPath(flowId, '/draft'),
    method: 'PUT',
    body: saveFlowDraftRequestSchema.parse(request),
    schema: flowResponseSchema,
  });
  return response.flow;
}

/* ---- preflight, launch, lifecycle ---------------------------------------- */

export async function preflightFlow(flowId: string): Promise<FlowPreflightResponse> {
  return apiRequest({ path: flowPath(flowId, '/preflight'), method: 'POST', schema: flowPreflightResponseSchema });
}

export function launchFlow(flowId: string): Promise<FlowDetail> {
  return flowCommand(flowId, '/launch');
}

export function pauseFlow(flowId: string): Promise<FlowDetail> {
  return flowCommand(flowId, '/pause');
}

export function resumeFlow(flowId: string): Promise<FlowDetail> {
  return flowCommand(flowId, '/resume');
}

export function archiveFlow(flowId: string, stopAll: boolean): Promise<FlowDetail> {
  return flowCommand(flowId, '/archive', archiveFlowRequestSchema.parse({ stopAll }));
}

export async function enrolFlow(flowId: string, request: FlowEnrolRequest): Promise<FlowEnrolResponse> {
  return apiRequest({
    path: flowPath(flowId, '/enrol'),
    method: 'POST',
    body: flowEnrolRequestSchema.parse(request),
    schema: flowEnrolResponseSchema,
  });
}

/* ---- test ----------------------------------------------------------------- */

export async function testFlow(flowId: string, request: FlowTestRequest, signal?: AbortSignal): Promise<FlowTestResponse> {
  return apiRequest({
    path: flowPath(flowId, '/test'),
    method: 'POST',
    body: flowTestRequestSchema.parse(request),
    schema: flowTestResponseSchema,
    signal,
  });
}

/**
 * Who the Test panel can walk the graph as.
 *
 * `GET /test-people` first. Where the API does not serve it (404/405), the
 * people Wend can already name for this flow, in this order: its enrolments,
 * the recent entrants of its rule segment, and Reach leads. Every one of those
 * is masked by the API before it leaves the server; nothing here unmasks.
 */
export async function fetchFlowTestPeople(
  flowId: string,
  segmentId: string | null,
  signal?: AbortSignal,
): Promise<FlowTestPeopleResponse> {
  try {
    return await apiRequest({ path: flowPath(flowId, '/test-people'), schema: flowTestPeopleResponseSchema, signal });
  } catch (error) {
    if (!isApiError(error) || (error.status !== 404 && error.status !== 405)) throw error;
  }

  const people = new Map<string, MaskedPerson>();
  const sources: string[] = [];
  const add = (list: readonly MaskedPerson[], source: string) => {
    let added = 0;
    for (const person of list) {
      if (people.size >= 20 || people.has(person.id)) continue;
      people.set(person.id, person);
      added += 1;
    }
    if (added > 0) sources.push(source);
  };
  const quietly = async <T,>(read: () => Promise<T>): Promise<T | null> => {
    try {
      return await read();
    } catch (error) {
      if (signal?.aborted) throw error;
      return null;
    }
  };

  const enrolments = await quietly(() => listFlowEnrollments(flowId, { page: 1, pageSize: 20 }, signal));
  if (enrolments) add(enrolments.items.map((item) => item.person), 'people already in this flow');
  if (segmentId !== null) {
    const events = await quietly(() =>
      apiRequest({
        path: `/segments/${encodeURIComponent(segmentId)}/membership-events`,
        query: { limit: '50' },
        schema: segmentMembershipEventsResponseSchema,
        signal,
      }),
    );
    if (events) add(events.events.filter((event) => event.kind === 'entered').map((event) => event.person), 'recent entrants of its segment');
  }
  const leads = await quietly(() =>
    apiRequest({ path: '/reach/leads', query: { page: '1', pageSize: '20' }, schema: reachLeadsResponseSchema, signal }),
  );
  if (leads) add(leads.items.map((lead) => lead.person), 'Reach leads');

  return {
    people: [...people.values()],
    source: sources.length > 0 ? `From ${sources.join(', ')}` : 'Nobody Wend can name for this flow yet',
  };
}

/* ---- run mode ------------------------------------------------------------- */

export async function fetchFlowStats(flowId: string, version: number | null, signal?: AbortSignal): Promise<FlowStatsResponse> {
  return apiRequest({
    path: flowPath(flowId, '/stats'),
    query: version === null ? {} : { version: String(version) },
    schema: flowStatsResponseSchema,
    signal,
  });
}

export interface FlowEnrollmentFilter {
  page: number;
  pageSize: number;
  node?: string;
  status?: FlowEnrollmentStatus;
  version?: number;
}

export async function listFlowEnrollments(
  flowId: string,
  filter: FlowEnrollmentFilter,
  signal?: AbortSignal,
): Promise<FlowEnrollmentListResponse> {
  return apiRequest({
    path: flowPath(flowId, '/enrollments'),
    query: {
      page: String(filter.page),
      pageSize: String(filter.pageSize),
      node: filter.node,
      status: filter.status,
      version: filter.version === undefined ? undefined : String(filter.version),
    },
    schema: flowEnrollmentListResponseSchema,
    signal,
  });
}

export async function fetchFlowEnrollment(
  flowId: string,
  enrollmentId: string,
  signal?: AbortSignal,
): Promise<FlowEnrollmentDetailResponse> {
  return apiRequest({
    path: flowPath(flowId, `/enrollments/${encodeURIComponent(enrollmentId)}`),
    schema: flowEnrollmentDetailResponseSchema,
    signal,
  });
}

/* ---- suggest -------------------------------------------------------------- */

export async function suggestFlowStep(flowId: string, request: FlowSuggestRequest): Promise<FlowSuggestResponse> {
  return apiRequest({
    path: flowPath(flowId, '/suggest'),
    method: 'POST',
    body: flowSuggestRequestSchema.parse(request),
    schema: flowSuggestResponseSchema,
  });
}

/* ---- the field catalogue (slice 67), as the builder reads it -------------- */

/**
 * `GET /fields`. Null — not an empty catalogue — when the endpoint is not
 * there, so the editor can say "fields are unavailable" rather than offering a
 * condition builder that looks like the workspace has no columns.
 */
export async function fetchFieldCatalogueForFlow(signal?: AbortSignal): Promise<FieldCatalogueResponse | null> {
  try {
    return await apiRequest({ path: '/fields', schema: fieldCatalogueResponseSchema, signal });
  } catch (error) {
    if (isApiError(error) && (error.status === 404 || error.status === 405)) return null;
    throw error;
  }
}
